import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import SendIcon from '@mui/icons-material/Send';
import AddCommentIcon from '@mui/icons-material/AddComment';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PublicIcon from '@mui/icons-material/Public';
import StorageIcon from '@mui/icons-material/Storage';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import firebase from '../../utilities/firebase';
import Store from '../../utilities/store';
import { StateManager } from '../../utilities/stateManager';
import MarkdownText from '../ChatMarkdown';
import moment from 'moment';

// Dev hits the local Functions emulator on Skyway's dedicated port (5051) — see
// skyway-functions/firebase.json. Moved off the default 5001 to avoid clashing
// with other apps' emulators.
const urlBase = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5051/skyway-dev-373d5/us-central1/'
  : 'https://us-central1-skyway-dev-373d5.cloudfunctions.net/';

const TOOL_LABELS = {
  web_search: 'Searching the web',
  get_car_details: 'Reading the car record',
  get_service_history: 'Reading service history',
  get_leads: 'Reading sales leads',
  get_deal_and_financials: 'Reading deal & financials',
  get_notes: 'Reading staff notes',
  get_car_photos: 'Looking at the photos',
  propose_car_update: 'Proposing a record edit',
};

const toolLabel = (name) => TOOL_LABELS[name] || `Using ${name}`;

const toolIcon = (name) => {
  if (name === 'web_search') return <PublicIcon sx={{ fontSize: 12 }} />;
  if (name === 'get_car_photos') return <PhotoLibraryIcon sx={{ fontSize: 12 }} />;
  if (name === 'propose_car_update') return <EditNoteIcon sx={{ fontSize: 12 }} />;
  return <StorageIcon sx={{ fontSize: 12 }} />;
};

// Render a proposed field value for the diff card / audit note (arrays, empties).
const fmtProposalVal = (v) => {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '(empty)';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// ── SSE streaming client for the carChat Cloud Function ──
async function streamCarChat({ stockNumber, messages, onText, onTool, onProposal, onError, onDone }) {
  let token = null;
  try {
    const user = firebase.auth && firebase.auth().currentUser;
    if (user) token = await user.getIdToken();
  } catch (e) { /* unauthenticated — backend will reject if it needs to */ }

  let res;
  try {
    res = await fetch(`${urlBase}carChat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ stockNumber, messages }),
    });
  } catch (e) {
    onError && onError(new Error('Could not reach the chat service.'));
    return;
  }

  if (!res.ok || !res.body) {
    onError && onError(new Error(`Chat request failed (${res.status}).`));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop();
    for (const chunk of chunks) {
      const dataLine = chunk.split('\n').find(l => l.startsWith('data:'));
      if (!dataLine) continue;
      const payload = dataLine.slice(5).trim();
      if (!payload) continue;
      let evt;
      try { evt = JSON.parse(payload); } catch (e) { continue; }
      if (evt.type === 'text') onText && onText(evt.text);
      else if (evt.type === 'tool') onTool && onTool(evt.name);
      else if (evt.type === 'proposal') onProposal && onProposal(evt);
      else if (evt.type === 'error') onError && onError(new Error(evt.error || 'Chat error'));
      else if (evt.type === 'done') onDone && onDone();
    }
  }
}

// ── Background chat sessions ───────────────────────────────────────────────
// A chat keeps streaming — and saves itself to Firestore — even if the user
// navigates to a different car and the Chat component unmounts. Sessions live
// here, keyed by stock number (only one in-flight chat per car, since the
// composer is locked while streaming). A mounted Chat mirrors its session via
// session.onUpdate; once it unmounts the stream keeps running against the
// session object, and a returning Chat re-attaches to see the live/finished
// response.
const liveChats = new Map();

const notifyChat = (session) => {
  if (session.onUpdate) {
    try { session.onUpdate(session); } catch (e) { /* component gone — ignore */ }
  }
};

// Drives one streamed assistant turn end-to-end: builds the session, streams
// tokens into it, then persists the full transcript to Firestore. The completed
// session lingers briefly so a component returning to this car can re-hydrate it.
function startChatStream({ stock, baseMsgs, existingChatId, isNew, title }) {
  const now = moment().format('YYYY/MM/DD HH:mm');
  const session = {
    stock,
    chatId: existingChatId || null,
    messages: [...baseMsgs, { role: 'assistant', text: '', tools: [], timestamp: now, streaming: true }],
    streaming: true,
    activeTool: null,
    onUpdate: null,
  };
  liveChats.set(stock, session);

  let text = '';
  let tools = [];
  let proposals = [];
  const patchAssistant = (patch) => {
    const msgs = session.messages.slice();
    msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], ...patch };
    session.messages = msgs;
    notifyChat(session);
  };

  (async () => {
    await streamCarChat({
      stockNumber: stock,
      messages: baseMsgs.map(m => ({ role: m.role, text: m.text })),
      onText: (t) => { text += t; patchAssistant({ text }); },
      onTool: (name) => {
        if (!tools.includes(name)) tools = [...tools, name];
        session.activeTool = name;
        patchAssistant({ tools });
      },
      onProposal: (p) => {
        proposals = [...proposals, {
          id: p.id,
          updates: p.updates || {},
          current: p.current || {},
          summary: p.summary || '',
          status: 'pending',
        }];
        patchAssistant({ proposals });
      },
      onError: (err) => {
        text = text ? text + `\n\n_(Error: ${err.message})_` : `Sorry — something went wrong: ${err.message}`;
        patchAssistant({ text });
        StateManager.setAlertAndOpen(`Chat error: ${err.message}`, 'error');
      },
    });

    session.streaming = false;
    session.activeTool = null;
    session.messages = [
      ...baseMsgs,
      { role: 'assistant', text, tools, ...(proposals.length ? { proposals } : {}), timestamp: moment().format('YYYY/MM/DD HH:mm') },
    ];

    try {
      if (!session.chatId) {
        const created = await Store.createCarChat(stock, title);
        session.chatId = created && created.id;
      }
      if (session.chatId) {
        await Store.saveCarChat(stock, session.chatId, { messages: session.messages, title: isNew ? title : undefined });
      }
    } catch (e) {
      StateManager.setAlertAndOpen('Could not save chat history.', 'warning');
    }

    notifyChat(session);
    // Release a finished session if nothing consumes it within a few minutes, so
    // a long-stale "done" chat never resurfaces in the pane on a much later visit.
    setTimeout(() => { if (liveChats.get(stock) === session) liveChats.delete(stock); }, 5 * 60 * 1000);
  })();

  return session;
}

export default function Chat({ car }) {
  const stock = car.stock;

  const [chats, setChats] = React.useState([]);
  const [activeChatId, setActiveChatId] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [streaming, setStreaming] = React.useState(false);
  const [activeTool, setActiveTool] = React.useState(null);

  const mountedRef = React.useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);

  const scrollRef = React.useRef(null);
  const atBottomRef = React.useRef(true);
  const rootRef = React.useRef(null);
  const [availH, setAvailH] = React.useState(null);

  // Size the chat to fill the modal viewport exactly, so the composer is always
  // pinned at the bottom (no scrolling to reach it) and only the transcript scrolls.
  const recomputeHeight = React.useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    let sc = root.parentElement;
    while (sc) {
      const oy = window.getComputedStyle(sc).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      sc = sc.parentElement;
    }
    const target = sc || document.documentElement;
    const cRect = target.getBoundingClientRect();
    const rRect = root.getBoundingClientRect();
    const offset = (rRect.top - cRect.top) + (target.scrollTop || 0);
    const gap = 20; // leaves room for the tab-content bottom padding
    const h = target.clientHeight - offset - gap;
    setAvailH(prev => {
      const next = Math.max(360, Math.round(h));
      return prev === next ? prev : next;
    });
  }, []);

  React.useLayoutEffect(() => {
    recomputeHeight();
  }, [recomputeHeight, car.car_loaded, car.deal_loaded, car.expenses_loaded]);

  React.useEffect(() => {
    recomputeHeight();
    window.addEventListener('resize', recomputeHeight);
    let ro;
    const root = rootRef.current;
    let sc = root && root.parentElement;
    while (sc) {
      const oy = window.getComputedStyle(sc).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      sc = sc.parentElement;
    }
    if (sc && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(recomputeHeight);
      ro.observe(sc);
    }
    return () => {
      window.removeEventListener('resize', recomputeHeight);
      if (ro) ro.disconnect();
    };
  }, [recomputeHeight]);

  const loadChats = React.useCallback(async () => {
    if (!stock) return;
    try {
      const list = await Store.getCarChats(stock);
      setChats(list);
    } catch (e) { /* listing failure is non-fatal */ }
  }, [stock]);

  React.useEffect(() => { loadChats(); }, [loadChats]);

  // Mirror a background chat session into this component's view. Guards against
  // a stale session updating after the user has switched cars / unmounted.
  const applySession = React.useCallback((s) => {
    if (!mountedRef.current || !s || s.stock !== stock) return;
    setMessages(s.messages);
    setStreaming(s.streaming);
    setActiveTool(s.activeTool);
    if (s.chatId) setActiveChatId(s.chatId);
    if (!s.streaming) loadChats();
  }, [stock, loadChats]);

  // On mount / car change, re-attach to any in-flight (or just-finished) chat
  // that was started for this car while we were away, so the response is present.
  React.useEffect(() => {
    const session = liveChats.get(stock);
    if (!session) return;
    session.onUpdate = applySession;
    applySession(session);
    // Finished while we were away: show it once, then release the session so a
    // later visit starts fresh instead of re-opening it.
    if (!session.streaming) liveChats.delete(stock);
    return () => { if (session.onUpdate === applySession) session.onUpdate = null; };
  }, [stock, applySession]);

  // Auto-scroll to the bottom only when the user is already near the bottom —
  // don't yank them down if they've scrolled up to read while it streams in.
  React.useEffect(() => {
    if (atBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, activeTool]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const startNewChat = () => {
    if (streaming) return;
    liveChats.delete(stock); // drop any lingering finished session for this car
    setActiveChatId(null);
    setMessages([]);
    setInput('');
  };

  const openChat = (chat) => {
    if (streaming) return;
    liveChats.delete(stock); // drop any lingering finished session for this car
    setActiveChatId(chat.id);
    setMessages(chat.messages || []);
  };

  const send = () => {
    const trimmed = input.trim();
    if (!trimmed || streaming || !stock) return;

    const now = moment().format('YYYY/MM/DD HH:mm');
    const userMsg = { role: 'user', text: trimmed, timestamp: now };
    const baseMsgs = [...messages, userMsg];

    atBottomRef.current = true; // jump to the latest when the user sends
    setInput('');

    // Hand the turn off to a background session so it keeps streaming and saves
    // even if the user navigates away; mirror it into this view while we're here.
    const session = startChatStream({
      stock,
      baseMsgs,
      existingChatId: activeChatId,
      isNew: !activeChatId,
      title: trimmed.slice(0, 48),
    });
    session.onUpdate = applySession;
    applySession(session);
  };

  const handleKeyDown = (e) => {
    // While streaming, let Enter insert a newline (don't submit) so the user can
    // keep composing their next message.
    if (e.key === 'Enter' && !e.shiftKey && !streaming) { e.preventDefault(); send(); }
  };

  // Update a single proposal's status within a message and persist the transcript.
  const patchProposal = async (msgIndex, proposalId, patch) => {
    const updated = messages.map((m, i) => i === msgIndex
      ? { ...m, proposals: (m.proposals || []).map(p => p.id === proposalId ? { ...p, ...patch } : p) }
      : m);
    setMessages(updated);
    try {
      if (activeChatId) await Store.saveCarChat(stock, activeChatId, { messages: updated });
    } catch (e) { /* persistence is best-effort; the UI state is already updated */ }
  };

  // Commit a proposed car-record edit: write the fields + an audit note, refresh
  // the live car modal, and sync search. Nothing is written until this runs.
  const applyProposal = async (msgIndex, proposal) => {
    if (!proposal || proposal.status !== 'pending') return;
    const updates = proposal.updates || {};
    const fields = Object.keys(updates);
    if (fields.length === 0) {
      StateManager.setAlertAndOpen('Nothing to apply — the proposal has no fields.', 'warning');
      return;
    }

    const me = StateManager.userName || 'Unknown';
    const changeLines = fields.map(f =>
      `${f}: ${fmtProposalVal(proposal.current ? proposal.current[f] : undefined)} → ${fmtProposalVal(updates[f])}`);
    const auditNote = {
      value: `AI Chat edit — ${changeLines.join('; ')}${proposal.summary ? ` (${proposal.summary})` : ''}`,
      timestamp: moment().format('YYYY/MM/DD HH:mm'),
      author: `${me} (AI Chat)`,
    };
    const allNotes = [...(car.notes || []), auditNote];

    try {
      StateManager.setLoading(true);
      await Store.applyCarChatUpdate(stock, updates, auditNote, allNotes);
      StateManager.updateCar({ ...updates, notes: allNotes });
      await patchProposal(msgIndex, proposal.id, { status: 'applied', appliedBy: me, appliedAt: moment().format('YYYY/MM/DD HH:mm') });
      StateManager.setAlertAndOpen('Car record updated.', 'success');
    } catch (e) {
      StateManager.setAlertAndOpen(`Could not apply changes: ${e.message}`, 'error');
    } finally {
      StateManager.setLoading(false);
    }
  };

  const dismissProposal = (msgIndex, proposal) => {
    if (!proposal || proposal.status !== 'pending') return;
    patchProposal(msgIndex, proposal.id, { status: 'dismissed' });
  };

  const fmtChatTime = (ts) => {
    if (!ts) return '';
    const m = ts.toDate ? moment(ts.toDate()) : moment(ts);
    if (!m.isValid()) return '';
    if (moment().diff(m, 'days') < 1) return m.format('h:mm a');
    if (moment().diff(m, 'days') < 7) return m.format('ddd');
    return m.format('MMM D');
  };

  return (
    <Box ref={rootRef} sx={{ mx: -2, mt: -2, display: 'flex', height: availH ? `${availH}px` : '72vh', minHeight: 360 }}>
      {/* ── Past chats sidebar ── */}
      <Box sx={{ width: { xs: 132, sm: 220 }, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', flexShrink: 0, bgcolor: 'grey.50' }}>
        <Box sx={{ p: 1 }}>
          <Button fullWidth size="small" variant="outlined" startIcon={<AddCommentIcon />} onClick={startNewChat} disabled={streaming}>
            New chat
          </Button>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 0.5, pb: 1 }}>
          {chats.length === 0 ? (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', textAlign: 'center', mt: 2, px: 1 }}>
              No past chats yet
            </Typography>
          ) : chats.map(chat => {
            const selected = chat.id === activeChatId;
            return (
              <Box
                key={chat.id}
                onClick={() => openChat(chat)}
                sx={{
                  px: 1, py: 0.75, mb: 0.25, borderRadius: 1, cursor: 'pointer',
                  bgcolor: selected ? 'primary.main' : 'transparent',
                  color: selected ? 'primary.contrastText' : 'text.primary',
                  '&:hover': { bgcolor: selected ? 'primary.main' : (t) => alpha(t.palette.action.hover, 0.6) },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 13, opacity: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" noWrap sx={{ fontWeight: selected ? 600 : 500, fontSize: '0.8rem' }}>
                    {chat.title || 'Chat'}
                  </Typography>
                </Box>
                <Typography variant="caption" noWrap sx={{ display: 'block', pl: 2.25, opacity: 0.7, fontSize: 10 }}>
                  {(chat.createdBy ? chat.createdBy.split(' ')[0] + ' · ' : '') + fmtChatTime(chat.updatedAt)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Conversation pane ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box ref={scrollRef} onScroll={handleScroll} sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, sm: 3 }, py: 2 }}>
          {messages.length === 0 ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'text.secondary', px: 2 }}>
              <SmartToyIcon sx={{ fontSize: 44, color: 'primary.light', mb: 1.5 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Ask about this {[car.year, car.make, car.model].filter(Boolean).join(' ') || 'car'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 460 }}>
                I can check our service history, leads, deal, and notes, and search the web to verify specs, market value, and originality. Ask me anything.
              </Typography>
            </Box>
          ) : messages.map((msg, i) => {
            const isUser = msg.role === 'user';
            return (
              <Box key={i} sx={{ display: 'flex', flexDirection: isUser ? 'row-reverse' : 'row', gap: 1.25, mb: 2, alignItems: 'flex-start' }}>
                {!isUser && (
                  <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25 }}>
                    <SmartToyIcon sx={{ fontSize: 17, color: 'white' }} />
                  </Box>
                )}
                <Box sx={{ maxWidth: isUser ? '78%' : '88%', minWidth: 0 }}>
                  {/* Tool activity chips for assistant turns */}
                  {!isUser && msg.tools && msg.tools.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                      {msg.tools.map((t, ti) => (
                        <Box key={ti} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.2, borderRadius: 1, bgcolor: 'grey.100', border: 1, borderColor: 'divider' }}>
                          {toolIcon(t)}
                          <Typography variant="caption" sx={{ fontSize: 10.5, color: 'text.secondary' }}>{toolLabel(t)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {isUser ? (
                    <Paper elevation={0} sx={{ px: 1.75, py: 1.1, bgcolor: 'primary.main', color: 'white', borderRadius: '14px 4px 14px 14px', display: 'inline-block' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' }}>{msg.text}</Typography>
                    </Paper>
                  ) : (
                    <Box sx={{ bgcolor: 'grey.100', borderRadius: '4px 14px 14px 14px', px: 1.75, py: 1.1 }}>
                      {msg.text
                        ? <MarkdownText text={msg.text} />
                        : (msg.streaming
                            ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                                <CircularProgress size={13} />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {activeTool ? toolLabel(activeTool) + '…' : 'Thinking…'}
                                </Typography>
                              </Box>
                            : <Typography variant="body2" sx={{ color: 'text.disabled' }}>(no response)</Typography>)
                      }
                    </Box>
                  )}

                  {/* Proposed car-record edits awaiting staff approval */}
                  {!isUser && msg.proposals && msg.proposals.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {msg.proposals.map((p) => {
                        const fields = Object.keys(p.updates || {});
                        return (
                          <Box key={p.id} sx={{ border: 1, borderColor: 'primary.light', borderRadius: 1.5, overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.75, bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}>
                              <EditNoteIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>Proposed record edit</Typography>
                            </Box>
                            {p.summary && (
                              <Typography variant="caption" sx={{ display: 'block', px: 1.25, pt: 0.75, color: 'text.secondary', fontStyle: 'italic' }}>{p.summary}</Typography>
                            )}
                            <Box sx={{ px: 1.25, py: 0.75 }}>
                              {fields.map((f) => (
                                <Box key={f} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.5, py: 0.25 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 96 }}>{f}</Typography>
                                  <Typography variant="caption" sx={{ color: 'text.disabled', textDecoration: 'line-through', wordBreak: 'break-word' }}>
                                    {fmtProposalVal(p.current ? p.current[f] : undefined)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>→</Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.dark', wordBreak: 'break-word' }}>
                                    {fmtProposalVal((p.updates || {})[f])}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, borderTop: 1, borderColor: 'divider' }}>
                              {p.status === 'applied' ? (
                                <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: 'success.dark', fontWeight: 600 }}>
                                  <CheckIcon sx={{ fontSize: 14 }} /> Applied{p.appliedBy ? ` by ${p.appliedBy}` : ''}{p.appliedAt ? ` · ${p.appliedAt}` : ''}
                                </Typography>
                              ) : p.status === 'dismissed' ? (
                                <Typography variant="caption" sx={{ color: 'text.disabled' }}>Dismissed</Typography>
                              ) : (
                                <>
                                  <Button size="small" variant="contained" startIcon={<CheckIcon />} disabled={streaming} onClick={() => applyProposal(i, p)}>
                                    Apply
                                  </Button>
                                  <Button size="small" color="inherit" startIcon={<CloseIcon />} disabled={streaming} onClick={() => dismissProposal(i, p)}>
                                    Dismiss
                                  </Button>
                                </>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ── Composer ── */}
        <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={6}
            size="small"
            placeholder="Ask about this car… (↵ to send, ⇧↵ for new line)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Tooltip title="Send">
            <span>
              <IconButton color="primary" onClick={send} disabled={!input.trim() || streaming} sx={{ flexShrink: 0, mb: 0.25 }}>
                {streaming ? <CircularProgress size={20} /> : <SendIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
